import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Receipt as ReceiptIcon, Search, Trash2, Pencil, X, Loader2, ImageIcon, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { money, dateFmt, categoryBadge } from "../lib/format";
import { ReceiptImage } from "../components/ReceiptImage";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";

export default function Receipts() {
  const qc = useQueryClient();
  const { role } = useAuth();
  const [q, setQ] = useState("");
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["expenses"], queryFn: async () => (await api.get("/expenses")).data });
  const expenses = (data?.expenses || []).filter((e) =>
    !q || e.name.toLowerCase().includes(q.toLowerCase()) || e.category.toLowerCase().includes(q.toLowerCase()) || e.submitter_name.toLowerCase().includes(q.toLowerCase())
  );

  const confirmDelete = async () => {
    try {
      await api.delete(`/expenses/${deleting.id}`);
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Expense deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <ReceiptIcon className="h-6 w-6 text-primary" /> Receipts & expenses
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">Everything the household has spent.</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, category or person" className="pl-9 h-11 rounded-xl" data-testid="receipts-search-input" />
      </div>

      {isLoading ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground" data-testid="receipts-empty">
          <ImageIcon className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No expenses found.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="receipt-gallery">
          {expenses.map((e) => (
            <div key={e.id} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="receipt-card">
              <div className="flex">
                <button onClick={() => e.receipt_file_id && setViewing(e)} className="h-28 w-28 shrink-0 bg-secondary" data-testid="receipt-thumb">
                  {e.receipt_file_id ? <ReceiptImage fileId={e.receipt_file_id} className="h-28 w-28 object-cover" /> : (
                    <div className="h-28 w-28 flex items-center justify-center text-muted-foreground"><ReceiptIcon className="h-7 w-7 opacity-40" /></div>
                  )}
                </button>
                <div className="flex-1 p-3 min-w-0 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-sm leading-tight">{e.name}</span>
                    <span className="font-heading font-bold tabular-nums text-sm shrink-0">{money(e.amount)}</span>
                  </div>
                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 mt-1 w-fit border ${categoryBadge(e.category)}`}>{e.category}{e.is_bill ? " · bill" : ""}</Badge>
                  <div className="text-[11px] text-muted-foreground mt-auto pt-2 flex items-center gap-1"><Calendar className="h-3 w-3" />{dateFmt(e.date)} · {e.submitter_name}</div>
                </div>
              </div>
              {role === "admin" && (
                <div className="flex border-t border-border divide-x divide-border">
                  <button onClick={() => setDeleting(e)} className="flex-1 py-2 text-xs font-medium text-rose-600 flex items-center justify-center gap-1.5 hover:bg-rose-50 dark:hover:bg-rose-950/30" data-testid="delete-expense-btn"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Receipt viewer */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="receipt-viewer-modal">
          {viewing && (
            <div>
              <ReceiptImage fileId={viewing.receipt_file_id} className="w-full max-h-[60vh] object-contain bg-secondary" />
              <div className="p-5 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading font-bold text-lg">{viewing.name}</h3>
                  <span className="font-heading font-bold text-lg">{money(viewing.amount)}</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={`border ${categoryBadge(viewing.category)}`}>{viewing.category}</Badge>
                  <span className="text-sm text-muted-foreground">{dateFmt(viewing.date)}</span>
                  <span className="text-sm text-muted-foreground">· Submitted by {viewing.submitter_name}</span>
                </div>
                {viewing.notes && <p className="text-sm text-muted-foreground pt-1">{viewing.notes}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this expense?</AlertDialogTitle>
            <AlertDialogDescription>"{deleting?.name}" ({money(deleting?.amount)}) will be removed and everyone's balance recalculated.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
